//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import type { ChatMessage } from './AIModule';
import type {
  ToolOutputRef,
  ToolResultBlock,
  ToolStatus,
} from '../../../types/streaming';

// Shared with ToolCallBox/ToolCallDetails via ToolStatus (AIModule.tsx) so a
// new status value can't silently mismatch between this file's aggregate
// counts and their rendering (found in review of #988).
const isTerminalToolStatus = (status: ToolStatus): boolean =>
  status === 'success' || status === 'error';
const isInFlightToolStatus = (status: ToolStatus): boolean =>
  status === 'running' || status === 'pending_approval';

// One exhaustive discriminator over ToolStatus, for a consumer that needs a
// different value per status (an icon, a color, a label) rather than a
// boolean. ToolCallBox.tsx and TurnTracePanel.tsx each used to branch over
// the same 4-way ToolStatus union independently to pick a visual — a
// deliberately different vocabulary each (spinner/chip/check/x vs. lucide
// icons; TurnTracePanel also layers a rejected-vs-failed distinction on top,
// which is a gateDecision concern orthogonal to ToolStatus, not part of this
// function) — but the underlying classification was duplicated, with no
// compiler check tying the two chains together: a 5th ToolStatus value could
// silently go unhandled in one of them (found in review of #988). Both now
// call this instead, supplying a `Record<ToolStatus, T>` — TypeScript
// requires every key at every call site, so a new status value is a compile
// error in both places, not a silent gap in one.
//
// `cases[status]` alone would drop the fallback both call sites had before
// this consolidation (each unconditionally fell through to its error visual
// for any status outside running/pending_approval/success) — the
// `Record<ToolStatus, T>` type only enforces that at compile time, not
// against a runtime value from a socket payload or restored-history entry
// that bypasses it. `?? cases.error` keeps that old safe default (found in
// review of #988, fourth round).
export const pickForToolStatus = <T>(
  status: ToolStatus,
  cases: Record<ToolStatus, T>,
): T => cases[status] ?? cases.error;

// Interim, name-based internal-call filter (FR-021). The durable fix is a
// backend-supplied `internal` marker on tool_call_start; until that exists,
// this is the one shared list of which tool names are internal — AIModule's
// tool_call_start handler also imports `isInternalToolName` from here
// (found in review of #988: it used to hardcode its own `=== 'submit_plan'`
// check, a second, independently-maintained copy of this same list; that
// tool was later renamed to `propose_plan` before ever shipping in a
// release, so the list tracks the new name only — no legacy alias needed).
// `call_tool` never reaches *this* file's filter at all — it's suppressed
// backend-side before it becomes a message. `propose_plan` has its own
// early-return guard at the tool_call_start handler in AIModule too, using
// this same list, but a duplicate/replayed event can still slip a row past
// that guard (e.g. via the tool_call_start replay-upsert path, which
// doesn't re-check the tool name) — listed here as well so it never
// reaches the trace panel regardless of how it got into message state.
// Both stay listed here either way, so this remains the single place a
// future internal tool gets added, not something the next author has to
// rediscover by reading two files. `search_tools` is the one meta-tool
// that reaches this filter directly and is the AC7 noise it removes.
export const INTERNAL_TOOL_NAMES: ReadonlySet<string> = new Set([
  'search_tools',
  'propose_plan',
  'call_tool',
]);

export const isInternalToolName = (toolName: string): boolean =>
  INTERNAL_TOOL_NAMES.has(toolName);

// The subset of INTERNAL_TOOL_NAMES that must never become a ChatMessage at
// all. Distinct from the full set above on purpose: `search_tools` IS
// internal (excluded from the visible trace/count by isInternalToolName
// above) but AC10 requires the downloaded transcript to keep every call —
// which only works if a message exists for `transcriptExport.ts` to pick up,
// even though `turnGrouping.ts`'s own display filter hides it from the UI.
// AIModule's tool_call_start handler used to gate on the full
// INTERNAL_TOOL_NAMES set, which also (incorrectly) suppressed search_tools
// before any message was ever created, silently dropping it from the
// transcript too (found in review of #988, fifth round). `call_tool` is
// listed for documentation only — it's suppressed backend-side before this
// handler ever sees it — `propose_plan` is the one this set actually acts on.
export const SUPPRESSED_BEFORE_MESSAGE_TOOL_NAMES: ReadonlySet<string> =
  new Set(['propose_plan', 'call_tool']);

export const isSuppressedBeforeMessage = (toolName: string): boolean =>
  SUPPRESSED_BEFORE_MESSAGE_TOOL_NAMES.has(toolName);

export interface ToolTraceRow {
  toolCallId: string;
  toolName: string;
  toolArgs: Record<string, unknown>;
  toolResult?: string;
  toolResultBlocks?: ToolResultBlock[];
  // Spec 007: the output was too large to keep as text; the row shows a size message and a Download button.
  toolOutputRef?: ToolOutputRef;
  status: ToolStatus;
  // Client-observed elapsed time in ms, when both endpoints are known.
  durationMs?: number;
  // Populated for a *restored* tool call, from the backend's own measured
  // duration (analyticsai-server PR #409; spec 006 convergence T022) — the
  // live event stream still reports none, so this stays undefined for a
  // live call and durationMs (above) is what's shown for those.
  reportedDurationMs?: number;
  wasGated: boolean;
  // Absent while a wasGated call's approval is still outstanding — that is
  // the one row whose card is still in the column too (S3b; FR-014a).
  gateDecision?: 'approved' | 'rejected';
}

// Single source for "does this row read as approved" (Principle VII —
// single-sourced derived state). Previously re-derived independently in
// TurnTracePanel.tsx as its own copy of this exact rule, which is what let
// it drift out of sync with approvedCount below once already (T062/T063:
// the summary line correctly said "N granted" while the per-row marker,
// keyed on gateDecision alone, showed fewer). Both now read this one
// function instead of each maintaining their own copy.
export const isApprovedForDisplay = (row: ToolTraceRow): boolean =>
  row.wasGated &&
  row.status !== 'pending_approval' &&
  row.gateDecision !== 'rejected';

export interface TurnGroup {
  // null identifies the single legacy bucket for messages stamped before
  // this feature existed, or restored from history (FR-002).
  turnId: string | null;
  messages: ChatMessage[];
  // Index of this turn's last message within the full, unfiltered `messages`
  // array passed to groupMessagesByTurn — recorded during the same pass that
  // builds the buckets above, so a consumer needing "where does this turn's
  // last message sit in the whole array" (ChatThread.tsx, to know where to
  // insert the turn artifact) never needs its own separate indexOf() scan
  // (found in review of #988: performance).
  lastMessageIndex: number;
  toolRows: ToolTraceRow[];
  visibleToolCount: number;
  completedToolCount: number;
  // visibleToolCount/completedToolCount with rejected calls excluded — a
  // rejected call never ran, so it must not count as "completed" alongside
  // one that did (found in review of #988). Computed once here, not at
  // each consumer, after `ChatThread.tsx` doing this subtraction locally
  // let it disagree with `TurnTraceControl`'s own render gate: a turn whose
  // only call was rejected computed a *displayToolCount* of 0 and used that
  // same 0 to also decide whether to render the control at all, so the
  // panel — if already open — lost its only way to close (found in review
  // of #988). Visibility must stay keyed on visibleToolCount (raw, below),
  // never on the rejected-excluded number.
  displayToolCount: number;
  displayCompletedCount: number;
  isRunning: boolean;
  // Narrower than isRunning: true only when a row is genuinely `running`,
  // never for `pending_approval` alone. isRunning conflates "actually
  // executing" with "waiting on a human," which let the control's label
  // claim "Running tools…" for a turn that was doing nothing but waiting on
  // an approval decision (found in review of #988, third round) — the
  // control now checks this field for that specific decision instead.
  hasActiveExecution: boolean;
  // Drive the per-turn approval summary line (FR-014b) — one line per turn
  // regardless of how many approvals it took, since gating is not bounded
  // (mutations today, potentially reads) and the default Approve button
  // grants scope: 'once', so a plan's mutations gate individually.
  approvedCount: number;
  rejectedCount: number;
}

const toToolTraceRow = (
  msg: Extract<ChatMessage, { sender: 'tool' }>,
): ToolTraceRow => ({
  toolCallId: msg.toolCallId,
  toolName: msg.toolName,
  toolArgs: msg.toolArgs,
  toolResult: msg.toolResult,
  toolResultBlocks: msg.toolResultBlocks,
  toolOutputRef: msg.toolOutputRef,
  status: msg.toolStatus,
  durationMs:
    msg.startedAt !== undefined && msg.endedAt !== undefined
      ? msg.endedAt - msg.startedAt
      : undefined,
  // T022 (spec 006 convergence): now populated for a restored tool call —
  // TurnTracePanel already prefers this over durationMs when present.
  reportedDurationMs: msg.reportedDurationMs,
  wasGated: msg.wasGated === true,
  gateDecision: msg.gateDecision,
});

/**
 * Groups a flat, turn-stamped message list into per-turn buckets, in the
 * order messages were created. Never infers a turn boundary from array
 * position — isTransientMessage trimming, the plan-expiry rewrite, and the
 * streaming-buffer replacement in AIModule all splice this array mid-turn,
 * so only the stamp itself is a reliable turn boundary (see research.md R2).
 */
export const groupMessagesByTurn = (messages: ChatMessage[]): TurnGroup[] => {
  const order: (string | null)[] = [];
  const byKey = new Map<string | null, ChatMessage[]>();
  // Recorded during this same single pass, not a second scan — ChatThread.tsx
  // used to find each turn's own position via a separate messages.indexOf()
  // per turn after this function returned, an O(turns × messages) rescan
  // riding on the same trigger (every streamed token) as this function's own
  // O(messages) pass (found in review of #988: performance). Since we're
  // already iterating in order here, the last index written for a key IS
  // that turn's last message's index — free to capture, nothing extra to
  // compute.
  const lastIndexByKey = new Map<string | null, number>();

  messages.forEach((msg, index) => {
    const key = 'turnId' in msg && msg.turnId ? msg.turnId : null;
    let bucket = byKey.get(key);
    if (!bucket) {
      bucket = [];
      byKey.set(key, bucket);
      order.push(key);
    }
    bucket.push(msg);
    lastIndexByKey.set(key, index);
  });

  return order.map((turnId) => {
    const groupMessages = byKey.get(turnId) ?? [];
    const toolRows = groupMessages
      .filter(
        (m): m is Extract<ChatMessage, { sender: 'tool' }> =>
          m.sender === 'tool' && !isInternalToolName(m.toolName),
      )
      .map(toToolTraceRow);

    const completedToolCount = toolRows.filter((r) =>
      isTerminalToolStatus(r.status),
    ).length;

    const gatedRows = toolRows.filter((r) => r.wasGated);
    // Keyed on status, not gateDecision alone (S3c, research.md R4 addendum
    // 3, extended per review of #989 finding 1): a call resolved via a
    // wider grant (or the backend) has no gateDecision either, but it did
    // resolve, and it didn't get explicitly rejected — a standing grant
    // only ever auto-approves, never auto-rejects, so "not pending and not
    // explicitly rejected" is a safe, symmetric definition of "approved"
    // that includes that case. rejectedCount stays keyed on gateDecision
    // alone: rejection is always an explicit local decision, never
    // something a wider grant produces silently.
    //
    // Known residual imprecision, not introduced here: a row abandoned via
    // a disconnect/settle path while genuinely still `pending_approval`
    // (never decided by anyone) is force-settled to `toolStatus: 'error'`
    // with no `gateDecision` either — indistinguishable, from this data
    // alone, from a wider-grant-approved call that ran and later failed
    // for real. Both read as "approved" here. Pre-existing ambiguity in
    // what the abandon-settle paths record, not something this fix can
    // resolve without also changing what they stamp on abandonment.
    const approvedCount = gatedRows.filter(isApprovedForDisplay).length;
    const rejectedCount = gatedRows.filter(
      (r) => r.gateDecision === 'rejected',
    ).length;
    const hasActiveExecution = toolRows.some((r) => r.status === 'running');

    return {
      turnId,
      messages: groupMessages,
      // Non-null assertion is safe: turnId only ever comes from `order`,
      // which is only ever populated (in the loop above) at the same moment
      // lastIndexByKey first gets an entry for that same key.
      lastMessageIndex: lastIndexByKey.get(turnId)!,
      toolRows,
      visibleToolCount: toolRows.length,
      completedToolCount,
      displayToolCount: toolRows.length - rejectedCount,
      displayCompletedCount: completedToolCount - rejectedCount,
      isRunning: toolRows.some((r) => isInFlightToolStatus(r.status)),
      hasActiveExecution,
      approvedCount,
      rejectedCount,
    };
  });
};
